import React, { useContext, useEffect, useRef, useState } from "react";
import { Box, Chip, Typography, Button, Tooltip } from "@mui/material";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useGetFamilyDetails, useUpdateFamily } from "services/advisor";
import ActivityFeed from "components/activity/ActivityFeed";
import Loading from "components/common/Loading";
import {
  Family,
  FamilyStatus,
  GetFamilyDetailsResponse_FamilyDashboardAlerts,
  ScheduledChange,
  ScheduledChange_Type,
  UpdateFamilyRequest,
} from "protogen/advisors_service_pb";
import {
  TaskInspiration,
  TaskInspirationCategory,
  TaskWishlistEntry,
} from "protogen/tasks_pb";
import { AgentPrompt } from "protogen/agents_service_pb";
import TwoPanelGrid, { Panel } from "components/details-page/TwoPanelGrid";
import TabbedDetailsPanel from "components/details-page/TabbedDetailsPanel";
import { Note } from "protogen/notes_pb";
import useIsMobile from "components/hooks/useIsMobile";
import { SearchContext } from "components/context/SearchContextProvider";
import FamilyTaskSection from "components/family/FamilyTaskSection";
import FamilySecretsSection from "components/family/FamilySecretsSection";
import Breadcrumbs from "components/common/Breadcrumbs";
import SuggestionActions, {
  SuggestionActionsHandle,
} from "components/activity/SuggestionActions";
import { useNavigate } from "react-router";
import { Advisor, UserPermission } from "protogen/common_pb";
import { PlanUtilization } from "protogen/common_pb";
import NotFound from "components/NotFound";
import useTitle from "components/hooks/useTitle";
import FamilyCalendarSection from "components/family/FamilyCalendarSection";
import FamilySettingsSection from "../components/family/FamilySettingsSection";
import ActivationDialog from "../components/family/ActivationDialog";
import ContactSection from "components/activity/ContactSection";
import { Medium } from "protogen/feed_pb";
import { CurrentUserContext } from "../components/context/RequireAuth";
import { isUserAdmin } from "../common/userUtils";
import FamilyAdminSection from "../components/family/FamilyAdminSection";
import FamilyDocumentsSection from "../components/family/FamilyDocumentsSection";
import NewFamilyActionsPrompt from "components/prompts/NewFamilyActionsPrompt";
import FamilyDashboardSection from "components/family/FamilyDashboardSection";

const NEW_FAMILY_URL_PARAM = "newFamily";

interface LeftFamilyPanelProps {
  family: Family;
  familyNotes: Note;
  primaryAdvisor: Advisor;
  advisorPermissions: UserPermission[];
  planUtilization: PlanUtilization;
  calendarRefreshTrigger: number;
  alerts: GetFamilyDetailsResponse_FamilyDashboardAlerts;
  taskInspirations?: TaskInspiration[];
  taskInspirationCategories?: TaskInspirationCategory[];
  magicDocs?: AgentPrompt[];
  taskWishlistEntries?: TaskWishlistEntry[];
}
const LeftFamilyPanel = ({
  family,
  familyNotes,
  primaryAdvisor,
  advisorPermissions,
  planUtilization,
  calendarRefreshTrigger,
  alerts,
  taskInspirations,
  taskInspirationCategories,
  magicDocs,
  taskWishlistEntries,
}: LeftFamilyPanelProps) => {
  const [searchParams] = useSearchParams();
  const currentUser = useContext(CurrentUserContext);
  let panels = [
    // <Section key={"Suggestions"} title={"Suggestions"}>
    //   <CardsContainer />
    // </Section>,
    <FamilyDashboardSection
      title={"Dashboard"}
      key={"dashboard"}
      tabName={"tab-dashboard"}
      family={family}
      planUtilization={planUtilization}
      alerts={alerts}
      taskInspirations={taskInspirations}
      taskInspirationCategories={taskInspirationCategories}
      magicDocs={magicDocs}
      taskWishlistEntries={taskWishlistEntries}
    />,
    <FamilyTaskSection
      title={"Tasks"}
      key={"Open Tasks"}
      tabName={"tab-tasks"}
      family={family}
    />,
    <FamilyDocumentsSection
      title={"Documents"}
      key={"documents"}
      tabName={"tab-documents"}
      family={family}
      note={familyNotes}
    />,
    <FamilyCalendarSection
      title={"Calendar"}
      key={"Calendar"}
      tabName={"tab-calendar"}
      family={family}
      fullHeight
      refreshTrigger={calendarRefreshTrigger}
      panelSx={{ padding: "8px 0 0 0" }}
    />,
    <FamilySecretsSection
      title={"Secrets"}
      tabName={"tab-secrets"}
      family={family}
      key="secrets"
    />,
    <FamilySettingsSection
      title={"Settings"}
      key={"settings"}
      tabName={"tab-settings"}
      family={family}
      primaryAdvisor={primaryAdvisor}
      advisorPermissions={advisorPermissions}
      planUtilization={planUtilization}
    />,
    ...(isUserAdmin(currentUser)
      ? [
          <FamilyAdminSection
            title={"Admin"}
            key={"administration"}
            tabName={"tab-admin"}
            family={family}
            primaryAdvisor={primaryAdvisor}
            advisorPermissions={advisorPermissions}
            planUtilization={planUtilization}
          />,
        ]
      : []),
  ];
  let defaultTab = undefined;
  if (searchParams.has("factRef") || searchParams.has(NEW_FAMILY_URL_PARAM)) {
    defaultTab = panels.findIndex((p) => p.props.tabName === "tab-documents");
  } else if (searchParams.has("secretRef")) {
    defaultTab = panels.findIndex((p) => p.props.tabName === "tab-secrets");
  }
  return (
    <TabbedDetailsPanel
      panels={panels}
      defaultTab={defaultTab}
      sx={{ overflowY: "hidden", paddingRight: "20px", gap: "0px" }}
    />
  );
};

const getFamilyBadge = (family: Family) => {
  const values = new Map<FamilyStatus, [string, string, string]>([
    [FamilyStatus.ACTIVE, ["Active", "#ECF4EC", "#198282"]],
    [
      FamilyStatus.PROSPECT,
      ["Prospect", "rgba(251, 242, 229, 0.95)", "#9A6518"],
    ],
    [FamilyStatus.DEACTIVATED, ["Inactive", "#F5F5F5", "#6B6E7B"]],
    [
      FamilyStatus.DIGITAL_DEACTIVATED,
      ["Digital Inactive", "#F5F5F5", "#6B6E7B"],
    ],
    [
      FamilyStatus.DIGITAL_DEACTIVATED_TRIAL,
      ["Digital Trial Ended", "#F5F5F5", "#6B6E7B"],
    ],
    [FamilyStatus.TEST, ["Demo", "#FFF", "#6B6E7B"]],
    [FamilyStatus.DEMO, ["Demo", "#FFF", "#6B6E7B"]],
  ]);
  if (!values.has(family.status)) {
    // Includes NOT_SET and ACTIVE
    return null;
  }
  const [copy, bgColor, textColor] = values.get(family.status)!;
  const chip = (
    <Chip
      label={copy}
      sx={{
        height: "35px",
        fontSize: "16px",
        fontWeight: "600",
        borderRadius: "100px",
        padding: "8px 12px",
        background: bgColor,
        span: { color: textColor },
        ...(bgColor === "#FFF" && { border: "1px solid #D4D4D4" }),
      }}
    />
  );
  if (family.status === FamilyStatus.ACTIVE && family.scheduledChanges.length) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: "6px",
          flexDirection: "column",
        }}
      >
        {chip}
        {family.scheduledChanges.map((change, i) => (
          <ScheduledChangeIndicator key={i} change={change} />
        ))}
      </Box>
    );
  }
  return chip;
};

const SCHEDULED_CHANGE_DISPLAY: Record<
  ScheduledChange_Type,
  { emoji: string; label: string } | null
> = {
  [ScheduledChange_Type.CANCELLATION]: { emoji: "⚠️", label: "Service ends" },
  [ScheduledChange_Type.PLAN_CHANGE]: { emoji: "🆕", label: "New Plan starts" },
  [ScheduledChange_Type.PAUSE]: { emoji: "⏸️", label: "Pause starts" },
  [ScheduledChange_Type.TYPE_UNSET]: null,
};

const ScheduledChangeIndicator = ({ change }: { change: ScheduledChange }) => {
  const display = SCHEDULED_CHANGE_DISPLAY[change.type];
  if (!display || !change.effectiveDate) {
    return null;
  }
  const formattedDate = new Date(change.effectiveDate).toLocaleDateString(
    "en-US",
    {
      month: "short",
      day: "numeric",
    },
  );
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}
    >
      <span style={{ fontSize: "13px", lineHeight: 1 }}>{display.emoji}</span>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: "12px", fontWeight: 500, whiteSpace: "nowrap" }}
      >
        {display.label} {formattedDate}
      </Typography>
    </Box>
  );
};

const ToggleTestFamilyAction = ({
  family,
  mobile,
}: {
  family: Family;
  mobile?: boolean;
}) => {
  const [status, setStatus] = useState(family.status);
  const { request: updateFamily } = useUpdateFamily();
  useEffect(() => {
    setStatus(family.status);
  }, [family.status]);
  const onToggle = async () => {
    const resp = await updateFamily(
      new UpdateFamilyRequest({
        familyRef: family.ref,
        updatedStatus:
          status === FamilyStatus.TEST
            ? FamilyStatus.TEST_HIDDEN
            : FamilyStatus.TEST,
      }),
    );
    if (resp?.family) {
      setStatus(resp.family.status);
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "end",
        ...(mobile ? { justifyContent: "center", padding: "12px" } : {}),
      }}
    >
      <Tooltip title="Hide from left navigation">
        <Button onClick={onToggle} size="small" variant="outlined">
          {status === FamilyStatus.TEST ? "Hide" : "Show"}
        </Button>
      </Tooltip>
    </Box>
  );
};

const ActivateAction = ({
  family,
  mobile,
}: {
  family: Family;
  mobile?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Box
      sx={{
        display: "flex",
        width: "124px",
        alignItems: "center",
        justifyContent: "end",
        gap: "16px",
        ...(mobile ? { justifyContent: "center", padding: "12px" } : {}),
      }}
    >
      <Button onClick={() => setOpen(true)} size="small">
        Start service
      </Button>
      <ActivationDialog family={family} open={open} setOpen={setOpen} />
    </Box>
  );
};
const getPanelHeaderActions = (
  family: Family,
  planUtilization?: PlanUtilization,
  mobile?: boolean,
  selectedPanel?: Panel,
  onMediumChange?: (m: Medium) => void,
) => {
  let module;
  if (mobile && selectedPanel === Panel.Right) {
    module = (
      <Box>
        <ContactSection
          feedFocusState={{
            selectedContact: family.familyMembers[0],
            selectedMedium: null,
            selectedEntryKey: null,
            replyToEmail: null,
            editEmailDraft: null,
            isDirty: false,
            groupText: false,
            groupRecipientRefs: [],
          }}
          setFeedFocusState={(s) => {
            onMediumChange && onMediumChange(s.selectedMedium!);
          }}
          familyRef={family.ref}
        />
      </Box>
    );
  } else if (family.status === FamilyStatus.PREACTIVATION) {
    module = <ActivateAction family={family} mobile={mobile} />;
  } else {
    module = (
      <>
        {!mobile && (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "end",
              gap: "16px",
              ...(mobile ? { justifyContent: "center", padding: "12px" } : {}),
            }}
          >
            {getFamilyBadge(family)}
            {family.status === FamilyStatus.TEST ||
            family.status === FamilyStatus.TEST_HIDDEN ? (
              <ToggleTestFamilyAction family={family} mobile={mobile} />
            ) : null}
          </Box>
        )}
      </>
    );
  }
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        alignItems: "end",
        justifyContent: "end",
        gap: "16px",
      }}
    >
      {module}
    </Box>
  );
};

export default () => {
  const { setFamily } = useContext(SearchContext);
  const navigate = useNavigate();
  const { hash } = useLocation();
  const defaultPanel = hash.match(/#tab-.+/) ? Panel.Left : Panel.Right;
  const isMobile = useIsMobile();
  const [selectedPanel, setSelectedPanel] = useState(defaultPanel);
  const [selectedMedium, setSelectedMedium] = useState<Medium | null>(null);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [calendarRefreshTrigger, setCalendarRefreshTrigger] = useState(0);
  let params = useParams();
  const actionsRef = useRef<SuggestionActionsHandle | null>(null);
  const { data, request, errorCode } = useGetFamilyDetails((r) => {
    if (r.family) {
      setFamily(r.family);
      setHasLoaded(true);
    }
  });
  useTitle(
    data?.family?.name ? `Families | ${data?.family?.name}` : "Families",
  );

  const onMediumChange = (m: Medium) => {
    setSelectedMedium(m);
  };

  useEffect(() => {
    // Clear it so we don't show the wrong family while loading.
    setHasLoaded(false);
    request({ familyRef: params.familyRef! });
  }, [params.familyRef]);
  if (errorCode === 404) return <NotFound title={"Family not found"} />;
  if (!hasLoaded || !data) {
    return <Loading />;
  }
  const {
    family,
    familyNotes,
    planUtilization,
    alerts,
    taskInspirations,
    taskInspirationCategories,
    magicDocs,
    taskWishlistEntries,
  } = data;
  // Not sure why these are nullable. TS+protobuf problem for another day.
  if (!family) {
    throw Error("Family type expected.");
  }
  const refresh = () => {
    request({ familyRef: params.familyRef! });
  };

  return (
    <>
      <TwoPanelGrid
        key={family.ref}
        defaultPanel={defaultPanel}
        headerAction={getPanelHeaderActions(
          family,
          planUtilization,
          isMobile,
          selectedPanel,
          onMediumChange,
        )}
        showHeaderAction={!isMobile || selectedPanel === Panel.Right}
        onChangePanel={(panel) => {
          setSelectedPanel(panel);
          panel === Panel.Right
            ? navigate("#", { replace: true })
            : // tab-0 will default to the first tab
              navigate("#tab-0", { replace: true });
        }}
        leftPanel={{
          title: (
            <Box>
              <Typography variant="display">{family.name}</Typography>
            </Box>
          ),
          subtitle: !isMobile ? (
            <Breadcrumbs breadcrumbs={[{ name: "Family details" }]} />
          ) : null,
          switchContent: <Typography variant="body">Family details</Typography>,
          content: (
            <>
              {isMobile ? getPanelHeaderActions(family, undefined, true) : null}
              <LeftFamilyPanel
                family={family}
                familyNotes={familyNotes}
                primaryAdvisor={data.primaryAdvisor}
                advisorPermissions={data.advisorPermissions}
                planUtilization={planUtilization}
                calendarRefreshTrigger={calendarRefreshTrigger}
                alerts={alerts!}
                taskInspirations={taskInspirations}
                taskInspirationCategories={taskInspirationCategories}
                magicDocs={magicDocs}
                taskWishlistEntries={taskWishlistEntries}
              />
            </>
          ),
        }}
        rightPanel={{
          sx: { height: "100%" },
          title: null,
          subtitle: isMobile ? (
            <Box sx={{}}>
              <Typography variant="h1Serif">{family.name}</Typography>
            </Box>
          ) : null,
          switchContent: <Typography variant="body">Activity feed</Typography>,
          content: family.familyMembers.length ? (
            <ActivityFeed
              family={data.family!}
              suggestionActions={{
                suggestTask: (t) => {
                  actionsRef.current?.suggestTask(t);
                },
                suggestFact: (f) => {
                  actionsRef.current?.suggestFact(f);
                },
                suggestEvent: (e) => {
                  actionsRef.current?.suggestEvent(e);
                },
              }}
              primaryAdvisor={data.primaryAdvisor}
              advisorPermissions={data.advisorPermissions}
              selectedMedium={selectedMedium}
            />
          ) : null,
        }}
      />
      <SuggestionActions
        ref={actionsRef}
        familyRef={family.ref}
        onFactSuggested={refresh}
        onEventSuggested={() => {
          refresh();
          // The calendar controls it's own state + data.  Manually trigger
          // a refresh from a parent component.
          setCalendarRefreshTrigger(calendarRefreshTrigger + 1);
        }}
        // These get redirected.
        // onTaskSuggested={refresh}
      />
      <NewFamilyActionsPrompt
        familyRef={family.ref}
        urlParamName={NEW_FAMILY_URL_PARAM}
        textMessage="Hi, I'm your new Faye Family Advisor. I'm excited to start checking off your to-do list! Looking forward to working together!"
      />
    </>
  );
};
